import React, { useMemo, useState } from "react";
import { Cloud, Eye, EyeOff, X } from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { Section, Field } from "@/components/storage/ConnectModalPrimitives";

/**
 * ConnectAzureModal — first-class Azure Blob Storage connection UX,
 * matching ConnectStorageModal's NAS flow and ConnectS3Modal's S3 flow.
 *
 * Field set lifted directly from Agents.jsx's "Add manually" CreateAgentModal
 * Azure branch (already correct — storage_account/container/prefix/
 * sas_token, matching backend AzureConfig exactly) rather than redesigned;
 * that form is generically labeled "planning" tooling, buried behind
 * "Add manually," not a first-class connect flow.
 *
 * Pass `editAgent` (an existing Azure agent doc) to edit in place instead
 * of creating a new one -- fields pre-fill from it, Save PATCHes
 * /agents/{id}/storage/azure. The SAS token is never pre-filled (the
 * server never sends it back); leaving it blank on save keeps the
 * currently stored credential unchanged.
 */
export default function ConnectAzureModal({ role = "source", editAgent = null, onClose, onSaved }) {
    const isEdit = !!editAgent;
    const initialAzure = editAgent?.azure || {};
    const hadCredential = !!initialAzure.has_credential;

    const [name, setName] = useState(editAgent?.name || "");
    const [storageAccount, setStorageAccount] = useState(initialAzure.storage_account || "");
    const [container, setContainer] = useState(initialAzure.container || "");
    const [prefix, setPrefix] = useState(initialAzure.prefix || "");
    const [sasToken, setSasToken] = useState("");
    const [showToken, setShowToken] = useState(false);

    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const displayName = useMemo(() => {
        if (name.trim()) return name.trim();
        if (storageAccount && container) return `${storageAccount}-${container}`;
        return storageAccount || "";
    }, [name, storageAccount, container]);

    const save = async () => {
        setErr("");
        if (!storageAccount || !container) {
            setErr("Please fill in the storage account and container");
            return;
        }
        if (!displayName) {
            setErr("Please name this connection");
            return;
        }
        if (!isEdit && !sasToken) {
            setErr("A SAS token is required");
            return;
        }
        setBusy(true);
        try {
            const azure = {
                storage_account: storageAccount,
                container,
                prefix: prefix || null,
                // Blank on an edit means "keep the stored SAS token" -- the
                // server never sends it back, so this field only ever
                // carries a REPLACEMENT the operator just typed.
                sas_token: sasToken || null,
            };
            if (isEdit) {
                const { data } = await http.patch(`/agents/${editAgent.id}/storage/azure`, { name: displayName, azure });
                onSaved(data);
            } else {
                const { data } = await http.post("/agents", {
                    name: displayName, role, kind: "azure", version: "1.0.0-azure", azure,
                });
                onSaved(data);
            }
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog">
            <div
                className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-surface shadow-2xl"
                data-testid="connect-azure-modal"
            >
                <div className="flex items-center justify-between border-b border-border px-5 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#0078D4] to-[#005a9e] text-white shadow-md">
                            <Cloud size={16} />
                        </div>
                        <div>
                            <div className="font-display text-lg font-bold">
                                {isEdit ? "Edit Azure Connection" : "Connect Azure Blob Storage"}
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                {isEdit ? editAgent.name : (role === "source" ? "Source Azure container" : "Destination Azure container")}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-border p-1.5 hover:border-foreground/40"
                        data-testid="connect-azure-close"
                        aria-label="Close"
                    >
                        <X size={13} />
                    </button>
                </div>

                <div className="space-y-4 p-5">
                    <Section title="Container">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Field label="Storage account" required>
                                <input
                                    value={storageAccount}
                                    onChange={(e) => setStorageAccount(e.target.value)}
                                    placeholder="mystorageaccount"
                                    data-testid="azure-account-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                            <Field label="Container" required>
                                <input
                                    value={container}
                                    onChange={(e) => setContainer(e.target.value)}
                                    placeholder="backups"
                                    data-testid="azure-container-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                        </div>
                        <div className="mt-3">
                            <Field label="Prefix (optional — scopes this connection to a folder within the container)">
                                <input
                                    value={prefix}
                                    onChange={(e) => setPrefix(e.target.value)}
                                    placeholder="datasets/2026/"
                                    data-testid="azure-prefix-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                        </div>
                    </Section>

                    <Section title="Credentials">
                        <Field label="SAS token" required={!isEdit}>
                            <div className="relative">
                                <input
                                    type={showToken ? "text" : "password"}
                                    value={sasToken}
                                    onChange={(e) => setSasToken(e.target.value)}
                                    placeholder={isEdit ? "Leave blank to keep current" : "sv=2024-...&sig=..."}
                                    autoComplete="new-password"
                                    data-testid="azure-sas-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 pr-9 font-mono text-xs"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowToken((s) => !s)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                                    aria-label={showToken ? "Hide token" : "Show token"}
                                    data-testid="azure-sas-toggle"
                                    tabIndex={-1}
                                >
                                    {showToken ? <EyeOff size={13} /> : <Eye size={13} />}
                                </button>
                            </div>
                            {isEdit && (
                                <div className="mt-1 text-[10px] text-muted-foreground" data-testid="azure-sas-edit-hint">
                                    {hadCredential
                                        ? "A SAS token is already stored — only fill this in to replace it."
                                        : "No SAS token is currently stored."}
                                </div>
                            )}
                        </Field>
                        <div className="mt-3 rounded-md border border-[#0078D4]/30 bg-[#0078D4]/5 p-3 font-mono text-[10px] leading-relaxed text-muted-foreground">
                            A SAS (Shared Access Signature) is scoped to one container, time-limited, and
                            revocable from the Azure Portal without rotating a full account key —
                            MoovBytes never needs an account key.
                        </div>
                    </Section>

                    <Section title="Display name">
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={displayName || "e.g. Production Archive (Azure)"}
                            data-testid="azure-name-input"
                            className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                    </Section>

                    {err && (
                        <div className="rounded-md border border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/5 px-3 py-2 text-xs text-[hsl(var(--destructive))]">
                            {err}
                        </div>
                    )}
                </div>

                <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-border bg-surface px-5 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase tracking-widest"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={busy || !storageAccount || !container}
                        data-testid="azure-save-btn"
                        className="rounded-md bg-gradient-to-r from-[#0078D4] to-[#005a9e] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white shadow-md hover:shadow-lg disabled:opacity-60"
                    >
                        {isEdit ? (busy ? "Saving…" : "Save Changes") : (busy ? "Connecting…" : "Connect Azure")}
                    </button>
                </div>
            </div>
        </div>
    );
}
